import type { Execution, WorkItemDetail } from '@deskworks/shared-types';
import { ACTIVE_EXECUTION_STATUSES } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Bot, Crown, Database, History, Tags } from 'lucide-react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { SessionLink } from '@/components/session-link';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { api } from '@/lib/api';
import { formatDuration } from '@/lib/format';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

const OUTCOME: Record<string, { label: string; tone: string }> = {
  complete: { label: t('delivered'), tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
  done: { label: t('decided'), tone: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' },
  waiting: { label: t('waiting'), tone: 'bg-amber-500/15 text-amber-800 dark:text-amber-300' },
  needs_approval: { label: t('needs approval'), tone: 'bg-amber-500/15 text-amber-800 dark:text-amber-300' },
  blocked: { label: t('blocked'), tone: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  failed: { label: t('failed'), tone: 'bg-red-500/15 text-red-700 dark:text-red-300' },
  continue: { label: t('continues'), tone: 'bg-muted text-muted-foreground' },
};

function outcomeOf(run: Execution) {
  if (ACTIVE_EXECUTION_STATUSES.includes(run.status)) return { label: t('running'), tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' };
  if (run.status === 'lost') return { label: t('lost'), tone: 'bg-red-500/15 text-red-700 dark:text-red-300' };
  if (run.status === 'cancelled') return { label: t('stopped'), tone: 'bg-muted text-muted-foreground' };
  return OUTCOME[run.outcome ?? ''] ?? { label: run.status, tone: 'bg-muted text-muted-foreground' };
}

/**
 * The runs of a Work Item (docs/features/self-organizing-desks.md): every assignment of one agent, started by the Lead
 * with a brief, kept as the record — agent, brief, result, duration, cost. The Lead's own runs sit in between.
 */
export function RunsSection({ item }: { item: WorkItemDetail }) {
  const runs = useQuery({ queryKey: qk.workItemExecutions(item.id), queryFn: () => api.workItems.executions(item.id) });
  const list = (runs.data ?? []).filter((r) => r.kind === 'work_item' || r.kind === 'lead').reverse();
  return (
    <Section
      icon={<History />}
      title={t('Runs')}
      description={t('Who worked on it, with which brief, and what each run delivered. Only the Lead starts runs.')}
    >
      {runs.isPending ? (
        <LoadingRows rows={2} />
      ) : runs.isError ? (
        <ErrorState error={runs.error} onRetry={() => void runs.refetch()} />
      ) : !list.length ? (
        <EmptyState compact title={t('No runs yet')} description={t('The Lead plans the work and starts the first run.')} />
      ) : (
        <ol className="flex flex-col gap-3">
          {list.map((run) => {
            const outcome = outcomeOf(run);
            const lead = run.kind === 'lead';
            return (
              <li key={run.id} className={cn('rounded-lg border px-3.5 py-2.5', lead && 'border-dashed bg-muted/30')}>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {lead ? <Crown className="size-3.5 text-amber-600" /> : <Bot className="size-3.5 text-indigo-600" />}
                  <span className="font-medium text-foreground/80">{run.agentName ?? t('Agent')}</span>
                  <span>{lead ? t('Lead run') : t('run')}</span>
                  <span className={cn('rounded-full px-1.5 py-px font-medium', outcome.tone)}>{outcome.label}</span>
                  {run.startedAt && <span>{formatDuration(run.startedAt, run.finishedAt)}</span>}
                  {run.costUsd ? <span>${run.costUsd.toFixed(2)}</span> : null}
                  <RelativeTime value={run.createdAt} />
                  <SessionLink executionId={run.id} workspaceId={item.workspaceId} />
                </div>
                {run.brief?.trim() && (
                  <div className="mt-1.5 text-sm">
                    <span className="text-xs font-medium text-muted-foreground">{lead ? t('Why:') : t('Brief:')} </span>
                    {lead ? run.brief : <Markdown text={run.brief.length > 1200 ? `${run.brief.slice(0, 1200)}…` : run.brief} />}
                  </div>
                )}
                {(run.result?.trim() || run.error?.trim()) && (
                  <div className="mt-1.5 text-sm">
                    <span className="text-xs font-medium text-muted-foreground">{run.error && !run.result ? t('Error:') : t('Result:')} </span>
                    <Markdown text={(run.result || run.error || '').slice(0, 2000)} />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Section>
  );
}

/** The item's type: the desk's own status value for it and its fields; the rows of the workspace database it belongs with. */
export function TypeAndLinksSection({ item }: { item: WorkItemDetail }) {
  const fields = Object.entries(item.fields ?? {});
  if (!item.typeId && !item.links?.length) return null;
  return (
    <Section icon={<Tags />} title={item.typeName ?? t('Linked data')} description={item.typeId ? t('The desk’s own kind of work, its fields and status.') : undefined}>
      <div className="flex flex-col gap-3 text-sm">
        {item.typeStatus && (
          <div>
            <span className="text-xs text-muted-foreground">{t('Status:')} </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">{item.typeStatus}</span>
          </div>
        )}
        {fields.length > 0 && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {fields.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}</dt>
                <dd className="break-words">{Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
        {item.links?.length > 0 && (
          <ul className="flex flex-col gap-1">
            {item.links.map((l) => (
              <li key={l.id} className="flex items-center gap-1.5 text-xs">
                <Database className="size-3.5 text-muted-foreground" />
                <span className="font-mono">
                  {l.table}#{l.rowKey}
                </span>
                {l.note && <span className="text-muted-foreground">— {l.note}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}
